import React from 'react';
import { FiActivity } from 'react-icons/fi';
import { useTheme } from '../../contexts/ThemeContext';

interface ExecutiveStripProps {
  data: any | null;
  loading: boolean;
}

const ExecutiveStrip: React.FC<ExecutiveStripProps> = ({ data, loading }) => {
  const { darkMode } = useTheme();

  if (loading) {
    return (
      <div className={`rounded-lg border-l-4 border-indigo-500 overflow-hidden ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        <div className="flex items-center gap-1 px-3 pt-1.5">
          <FiActivity className="w-3 h-3 text-indigo-400" />
          <span className={`text-[9px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>KPIs</span>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-px">
          {[...Array(4)].map((_, i) => (
            <div key={i} className={`h-12 animate-pulse ${darkMode ? 'bg-gray-800' : 'bg-gray-100'}`} />
          ))}
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className={`rounded-lg border-l-4 border-indigo-500 overflow-hidden ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
        <div className="flex items-center gap-1 px-3 pt-1.5">
          <FiActivity className="w-3 h-3 text-indigo-400" />
          <span className={`text-[9px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>KPIs</span>
        </div>
        <div className={`text-xs text-center py-3 ${darkMode ? 'text-gray-600' : 'text-gray-500'}`}>
          Executive metrics unavailable.
        </div>
      </div>
    );
  }

  const healthScore = data.health_score ?? data.completion_rate ?? 0;
  const velocity = data.velocity ?? data.tasks_per_day ?? 0;
  const completedWeek = data.completed_this_week ?? data.completed ?? 0;
  const blocked = data.blocked_count ?? data.blocked ?? 0;

  const healthColor = healthScore > 70 ? 'text-emerald-400' : healthScore > 40 ? 'text-amber-400' : 'text-red-400';
  const velocityArrow = velocity > 3 ? '\u2191' : velocity > 1 ? '\u2192' : '\u2193';
  const blockedColor = blocked > 0 ? 'text-red-400' : 'text-emerald-400';

  const cells = [
    { label: 'Health', value: `${Math.round(healthScore)}/100`, color: healthColor },
    { label: 'Velocity', value: `${velocity.toFixed(1)}/d ${velocityArrow}`, color: darkMode ? 'text-gray-300' : 'text-gray-700' },
    { label: 'Done/wk', value: String(completedWeek), color: darkMode ? 'text-gray-300' : 'text-gray-700' },
    { label: 'Blocked', value: String(blocked), color: blockedColor },
  ];

  return (
    <div className={`rounded-lg border-l-4 border-indigo-500 overflow-hidden ${darkMode ? 'bg-gray-800/60' : 'bg-white'}`}>
      <div className="flex items-center gap-1 px-3 pt-1.5">
        <FiActivity className="w-3 h-3 text-indigo-400" />
        <span className={`text-[9px] font-semibold uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>KPIs</span>
      </div>
      <div className={`grid grid-cols-2 md:grid-cols-4 divide-x ${
        darkMode ? 'divide-gray-700' : 'divide-gray-200'
      }`}>
        {cells.map(cell => (
          <div key={cell.label} className="px-3 py-2 text-center">
            <div className={`text-lg font-bold ${cell.color}`}>{cell.value}</div>
            <div className={`text-[9px] uppercase tracking-wider ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>{cell.label}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ExecutiveStrip;
